/**
 * Access-token store + session bootstrap.
 *
 * The JWT access token lives in memory — never localStorage — so XSS can't
 * harvest a long-lived credential. The refresh token rides in an HttpOnly
 * cookie that JS can't read; on page reload `bootstrapSession()` calls the
 * refresh endpoint once to mint a fresh access token from the cookie. That
 * silent refresh is the ONLY thing that restores a session on reload — there
 * is no localStorage token fallback.
 */

import axios from "axios";

const CSRF_COOKIE_NAME = "csrf_refresh";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

let accessToken: string | null = null;
const listeners = new Set<() => void>();

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  for (const listener of listeners) listener();
}

export function clearAccessToken(): void {
  setAccessToken(null);
}

export function subscribeAccessToken(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// ─── Refresh helpers ──────────────────────────────────────────────────────

export function readCsrfRefreshCookie(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${CSRF_COOKIE_NAME}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : null;
}

interface RefreshResponse {
  access: string;
}

let inflightRefresh: Promise<string | null> | null = null;

/**
 * Single-flight refresh: concurrent 401s share one in-flight POST so we
 * don't fire N parallel refreshes against the (non-rotating) refresh cookie.
 *
 * Returns the new access token on success, `null` when the refresh cookie
 * is missing/expired/invalid (caller should treat as logged-out).
 */
export function refreshAccessToken(): Promise<string | null> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = doRefresh().finally(() => {
    inflightRefresh = null;
  });
  return inflightRefresh;
}

async function doRefresh(): Promise<string | null> {
  const csrf = readCsrfRefreshCookie();
  if (!csrf) return null;
  try {
    const { data } = await axios.post<RefreshResponse>(
      `${API_BASE_URL}/api/users/token/refresh/`,
      null,
      {
        withCredentials: true,
        headers: { "X-CSRF-Refresh": csrf },
      },
    );
    setAccessToken(data.access);
    return data.access;
  } catch {
    return null;
  }
}

/**
 * Called once on app boot. Restores the session purely from the HttpOnly
 * refresh cookie (silent refresh); otherwise leaves us anonymous and the
 * route guards bounce to /login.
 */
export async function bootstrapSession(): Promise<void> {
  // One-time sweep of the pre-JWT DRF token key. It's no longer read, but
  // clearing it keeps old sessions' localStorage tidy.
  localStorage.removeItem("auth_token");
  if (accessToken) return;
  await refreshAccessToken();
}
